import type { ComponentType } from "react";
import { useTranslation } from "@privacy/translations";
import { useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../../dynamicGrid";
import { DynamicHistory } from "../../dynamicHistory";
import { Drawer } from "../../shared/Drawer";
import type { IconProps } from "../../shared/icons";
import { PersonIcon3, FileTextIcon, TenantIcon3 } from "../../shared/icons";
import { logUnhandled } from "../../api/logError";
import { dpiaByIdQueryOptions, dpiaHistoryQueryOptions } from "../../api/dpia";
import type { DpiaStatus } from "../../api/dpia.types";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { DpiaStatusChip } from "./DpiaStatusChip";
import { DpiaOverviewDrawerHeader } from "./DpiaOverviewDrawerHeader";

export const DpiaHistoryDrawer = () => {
  const dpiaId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const dpiaQuery = useQuery(dpiaByIdQueryOptions(dpiaId));
  const historyQuery = useQuery(dpiaHistoryQueryOptions(dpiaId));

  return (
    <DynamicGrid.Drawer>
      {!dpiaQuery.data ? (
        <Drawer.Loading />
      ) : (
        <>
          <DpiaOverviewDrawerHeader
            dpia={dpiaQuery.data}
            activeTab="history"
            onDrawerClose={() => navigate(paths.dpiaGrid())}
          />
          {!historyQuery.data ? (
            <Drawer.Loading />
          ) : (
            <DynamicHistory.Entries
              historyEntries={historyQuery.data.history}
              renderCustomEntry={entry => {
                switch (entry.customType) {
                  case "dpiaCreated":
                    return <DynamicHistory.CreatedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />;
                  case "dpiaStatus":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Status")}
                        before={<DpiaHistoryStatus value={entry.oldValue} />}
                        after={<DpiaHistoryStatus value={entry.newValue} />}
                      />
                    );
                  case "dpiaName":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Name")}
                        before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
                        after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
                      />
                    );
                  case "dpiaResponsible":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Responsible")}
                        before={
                          <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
                            {entry.oldValue.firstName} {entry.oldValue.lastName}
                          </DynamicHistory.SnapshotValueText>
                        }
                        after={
                          <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
                            {entry.newValue.firstName} {entry.newValue.lastName}
                          </DynamicHistory.SnapshotValueText>
                        }
                      />
                    );
                  case "dpiaParticipants":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Participants")}
                        before={<DpiaHistoryParticipants>{entry.oldValue}</DpiaHistoryParticipants>}
                        after={<DpiaHistoryParticipants>{entry.newValue}</DpiaHistoryParticipants>}
                      />
                    );
                  case "dpiaSummary":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Summary")}
                        before={<DpiaHistoryValue>{entry.oldValue}</DpiaHistoryValue>}
                        after={<DpiaHistoryValue>{entry.newValue}</DpiaHistoryValue>}
                      />
                    );
                  case "dpiaUrl":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("Dpia.History.Url")}
                        before={<DpiaHistoryValue Icon={FileTextIcon}>{entry.oldValue}</DpiaHistoryValue>}
                        after={<DpiaHistoryValue Icon={FileTextIcon}>{entry.newValue}</DpiaHistoryValue>}
                      />
                    );
                  case "dpiaConnectedTenant":
                    return (
                      <DynamicHistory.BeforeAndAfterEntry
                        {...entry}
                        header={t("DpiaDrawer.ConnectedTenant.Label")}
                        before={
                          <DynamicHistory.SnapshotValueText Icon={TenantIcon3}>
                            {entry.oldValue.name}
                          </DynamicHistory.SnapshotValueText>
                        }
                        after={
                          <DynamicHistory.SnapshotValueText Icon={TenantIcon3}>
                            {entry.newValue.name}
                          </DynamicHistory.SnapshotValueText>
                        }
                      />
                    );
                  default:
                    return logUnhandled(entry, "DPIA custom entry");
                }
              }}
            />
          )}
        </>
      )}
    </DynamicGrid.Drawer>
  );
};

const DpiaHistoryParticipants = ({ children }: { children: string | undefined }) => {
  if (!children) return <DynamicHistory.EmptyValue />;
  return (
    <DynamicHistory.MultipleValues>
      {children.split(", ").map(name => (
        <DynamicHistory.SnapshotValueText Icon={PersonIcon3} key={name}>
          {name}
        </DynamicHistory.SnapshotValueText>
      ))}
    </DynamicHistory.MultipleValues>
  );
};

const DpiaHistoryValue = ({ Icon, children }: { Icon?: ComponentType<IconProps>; children: string | undefined }) => {
  if (!children) return <DynamicHistory.EmptyValue />;
  return <DynamicHistory.SnapshotValueText Icon={Icon}>{children}</DynamicHistory.SnapshotValueText>;
};

const DpiaHistoryStatus = ({ value }: { value: DpiaStatus }) => {
  return value === "preAssessment" ? (
    <DynamicHistory.SnapshotValueText>{value}</DynamicHistory.SnapshotValueText>
  ) : (
    <DpiaStatusChip status={value} />
  );
};
